import React from 'react';
import { getBookStatusColor, getBookStatusLabel } from '../utils/bookStatus';

interface BookOverviewTabProps {
  book: any;
  onEditBook: () => void;
  onEditCover: () => void;
  onEditDescription: () => void;
  onDuplicateBook: () => void;
  onExportBook: () => void;
  isExporting: boolean;
  onSubmitApproval?: () => void;
  onApprove?: () => void;
  onPublish?: () => void;
  isPublishing?: boolean;
  publishedBook?: any;
  isLoadingPublished?: boolean;
}

const BookOverviewTab: React.FC<BookOverviewTabProps> = ({
  book,
  onEditBook,
  onEditCover,
  onEditDescription,
  onDuplicateBook,
  onExportBook,
  isExporting,
  onSubmitApproval,
  onApprove,
  onPublish,
  isPublishing = false,
  publishedBook,
  isLoadingPublished = false
}) => {
  const getBookPrice = () => {
    const value = book?.price;
    const rawPrice = value && typeof value === 'object' ? value.amount : value;
    const price = Number(rawPrice);

    return Number.isFinite(price) && price > 0 ? price : null;
  };

  const bookPrice = getBookPrice();

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(price);

  const formatDate = (dateString?: string) => {
    const date = dateString ? new Date(dateString) : null;
    if (!date || Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Information */}
      <div className="lg:col-span-2 space-y-6">
        {/* Book Header Information */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">{book.title}</h1>
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full ${getBookStatusColor(
                    book.status
                  )}`}
                >
                  {getBookStatusLabel(book.status)}
                </span>
              </div>
              {book.subtitle && <p className="text-lg text-gray-600 mb-4">{book.subtitle}</p>}
              {!book.subtitle && (
                <p className="text-lg text-gray-600 mb-4">Sách định nghĩa cơ bản</p>
              )}

              {/* Basic Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <svg
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                  </svg>
                  <span className="font-medium">Danh mục:</span>
                  <span className="ml-1">{book.category}</span>
                </div>
                {book.grade && (
                  <div className="flex items-center text-sm text-gray-600">
                    <svg
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                      />
                    </svg>
                    <span className="font-medium">Lớp:</span>
                    <span className="ml-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {book.grade}
                    </span>
                  </div>
                )}
                {book.subject && (
                  <div className="flex items-center text-sm text-gray-600">
                    <svg
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                    <span className="font-medium">Môn học:</span>
                    <span className="ml-1 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                      {book.subject}
                    </span>
                  </div>
                )}
              </div>

              {/* Detailed Information Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mã sách</label>
                  <p className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded-md truncate">
                    #{book.id}
                  </p>
                </div>
                {bookPrice !== null && (
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Giá</label>
                    <p className="text-base font-semibold tabular-nums text-gray-900">
                      {formatPrice(bookPrice)}
                    </p>
                  </div>
                )}
                {book.authors && book.authors.length > 0 && (
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tác giả</label>
                    <div className="flex flex-wrap gap-1">
                      {book.authors.map((author: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap"
                        >
                          {author}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {book.publisher && (
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nhà xuất bản
                    </label>
                    <p className="text-sm text-gray-900 truncate">{book.publisher}</p>
                  </div>
                )}
                {book.language && (
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ngôn ngữ</label>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 whitespace-nowrap">
                      {book.language === "vi"
                        ? "Tiếng Việt"
                        : book.language === "en"
                        ? "English"
                        : book.language === "fr"
                        ? "Français"
                        : book.language === "de"
                        ? "Deutsch"
                        : book.language === "es"
                        ? "Español"
                        : book.language === "zh"
                        ? "中文"
                        : book.language === "ja"
                        ? "日本語"
                        : book.language === "ko"
                        ? "한국어"
                        : book.language}
                    </span>
                  </div>
                )}
                {book.categories && book.categories.length > 0 && (
                  <div className="min-w-0 sm:col-span-2 xl:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Danh mục</label>
                    <div className="flex flex-wrap gap-1">
                      {book.categories.map((category: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {book.tags && book.tags.length > 0 && (
                  <div className="min-w-0 sm:col-span-2 xl:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <div className="flex flex-wrap gap-1">
                      {book.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {book.version && (
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phiên bản
                    </label>
                    <p className="text-sm text-gray-900 truncate">{book.version}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onEditBook}
                className="bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                title="Chỉnh sửa"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Book Cover Image */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Ảnh bìa sách</h3>
            <button
              onClick={onEditCover}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center"
              title="Chỉnh sửa bìa"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>
          <div className="flex items-center justify-center">
            {book.cover_image_url ? (
              <div className="relative group">
                <img
                  src={book.cover_image_url}
                  alt={`Bìa sách ${book.title}`}
                  className="w-48 h-64 object-cover rounded-lg shadow-md border"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    target.nextElementSibling?.classList.remove("hidden");
                  }}
                />
                <div className="hidden inset-0 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <svg
                      className="w-16 h-16 mx-auto mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-sm">Không thể hiển thị ảnh</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-48 h-64 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                <div className="text-center text-gray-500">
                  <svg
                    className="w-16 h-16 mx-auto mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-sm">Chưa có ảnh bìa</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Book Description */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Mô tả sách</h3>
            <button
              onClick={onEditDescription}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center"
              title="Chỉnh sửa mô tả"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          </div>
          <div
            className="text-gray-600 leading-relaxed prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-ul:text-gray-600 prose-ol:text-gray-600 prose-li:text-gray-600 prose-strong:text-gray-900 prose-em:text-gray-700 prose-blockquote:text-gray-500 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: book.description || "" }}
          />
          {!book.description && <p className="text-gray-400 italic">Chưa có mô tả sách</p>}
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Status Card */}
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Trạng thái
          </h3>
          
          {/* Basic Status Info - Compact Grid */}
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Trạng thái</span>
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getBookStatusColor(
                  book.status
                )}`}
              >
                {getBookStatusLabel(book.status)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Ngày tạo</span>
              <span className="text-xs text-gray-900">{formatDate(book.created_at || book.createdDate)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Cập nhật cuối</span>
              <span className="text-xs text-gray-900">{formatDate(book.updated_at || book.lastModified)}</span>
            </div>
          </div>
          
          {/* Published Book Information - Compact */}
          {publishedBook && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
              <div className="flex items-center mb-2">
                <svg className="w-4 h-4 text-green-600 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="text-xs font-semibold text-green-800">Phiên bản đã xuất bản</h4>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-green-700 font-medium">Phiên bản:</span>
                  <span className="ml-1 text-green-600">{publishedBook.version || '1.0'}</span>
                </div>
                <div>
                  <span className="text-green-700 font-medium">Số phần:</span>
                  <span className="ml-1 text-green-600">{publishedBook.metadata?.total_sections || 0}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-green-700 font-medium">Ngày xuất bản:</span>
                  <span className="ml-1 text-green-600">
                    {publishedBook.published_at ? new Date(publishedBook.published_at).toLocaleDateString('vi-VN') : 'N/A'}
                  </span>
                </div>
                {publishedBook.metadata && (
                  <div className="col-span-2 pt-1 border-t border-green-200">
                    <div className="flex justify-between text-xs text-green-600">
                      <span>Thời lượng: {publishedBook.metadata.total_duration || 0} phút</span>
                      <span>Files: {publishedBook.metadata.file_count || 0}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Approval Actions */}
          <div className="pt-3 border-t border-gray-200 space-y-2">
            {(book.status === 'draft' || book.status === 'nháp') && onSubmitApproval && (
              <button
                onClick={onSubmitApproval}
                className="w-full flex items-center justify-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
              >
                <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                Gửi phê duyệt
              </button>
            )}
            
            {book.status === 'pending_approval' && onApprove && (
              <button
                onClick={onApprove}
                className="w-full flex items-center justify-center px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors"
              >
                <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Phê duyệt
              </button>
            )}
            
            {onPublish && (
              <button
                onClick={onPublish}
                disabled={isPublishing}
                className={`w-full flex items-center justify-center px-3 py-1.5 text-white text-xs font-medium rounded-md transition-colors ${
                  isPublishing 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : publishedBook 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isPublishing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-1.5 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Đang xuất bản...
                  </>
                ) : publishedBook ? (
                  <>
                    <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Xuất bản lại
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Xuất bản
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Thao tác nhanh</h3>
          <div className="space-y-3">
            <button
              onClick={onDuplicateBook}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-50"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Sao chép sách
            </button>
            <button
              onClick={onExportBook}
              disabled={isExporting}
              className={`w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                isExporting
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "text-gray-700 bg-white hover:bg-gray-50"
              }`}
            >
              {isExporting ? (
                <>
                  <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Đang xuất...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Xuất sách
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookOverviewTab;
